import React, { Suspense, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { isSafari, isIE, isMobileDevice } from 'utils/browserCheck';
import { fileTypes } from './ziggeoHelpers';

const AsyncReactZiggeoRecorder = React.lazy(() =>
  import(/* webpackChunkName: "ReactZiggeo" */ './ReactZiggeoRecorder'),
);

const ZiggeoMediaRecorder = props => {
  const safari = isSafari();
  const internetExplorer = isIE();
  const mobileDevice = isMobileDevice();

  const VIDEO_PROFILE = '_lessonly_video_profile';

  const {
    apiKey,
    recordWebcamEvent = () => {},
    handleBadUploadType,
    saveMedia,
    setUploadStarted,
    setUploadingComplete,
    isScreenRecording = false,
    isAudioRecording = false,
    webcamPlaybackEnabled = false,
    allowUpload = false,
    allowWebcam = true,
    multiStreamEnabled = false,
    mediaLocales = null,
    onUploading = () => {},
    onRecording = () => {},
    onBrowserRecordingNotSupported = () => {},
    id,
    isSeismicRecording,
    setShowRecorder,
    allowResponseUpload = false,
    seismicRecordingVideoElementEnabled = false,
    seismicPracticeRecordingEnabled = false,
  } = props;

  useEffect(() => {
    if (safari || internetExplorer || mobileDevice) {
      onBrowserRecordingNotSupported('all');
    }
  }, [safari, internetExplorer, onBrowserRecordingNotSupported, mobileDevice]);

  const recordEvent = data => {
    // TODO - update with relevant audio and screen rec events
    if (isAudioRecording) return () => {};
    if (isScreenRecording) return () => {};
    return recordWebcamEvent('video_response/webcam_recording', data.video);
  };

  const uploadEvent = data => {
    if (isAudioRecording) return () => {};
    if (isScreenRecording) return () => {};
    return recordWebcamEvent('video_response/webcam_uploading', data.video);
  };

  const verifiedEvent = data => {
    if (isAudioRecording) return () => {};
    if (isScreenRecording) return () => {};
    return recordWebcamEvent('video_response/webcam_verified', data.video);
  };

  const ziggeoPlayerUrl = 'https://ziggeo.io/p/';

  return (
    <div
      className={cn('ZiggeoRecorderContainer minh-240', {
        isSeismicRecording,
      })}
    >
      <Suspense fallback={null}>
        <AsyncReactZiggeoRecorder
          className={cn('ZiggeoRecorder', {
            SeismicRecordingEnabled: isSeismicRecording,
            SeismicRecordingVideoElementEnabled:
              seismicRecordingVideoElementEnabled,
            SeismicPracticeRecordingEnabled: seismicPracticeRecordingEnabled,
            allowZiggeoRecorder: !allowUpload,
            'mt-16': allowResponseUpload,
          })}
          id={id}
          localplayback={webcamPlaybackEnabled}
          allowmultistreams={
            !seismicRecordingVideoElementEnabled &&
            multiStreamEnabled &&
            !safari &&
            !internetExplorer &&
            !mobileDevice
          }
          apiKey={apiKey}
          width={!allowUpload ? '' : '100%'}
          height="auto"
          allowscreen={isScreenRecording && !safari && !internetExplorer}
          allowupload={allowUpload}
          allowrecord={
            !isSeismicRecording &&
            !seismicPracticeRecordingEnabled &&
            !seismicRecordingVideoElementEnabled &&
            allowWebcam &&
            !safari &&
            !mobileDevice
          }
          onlyaudio={isAudioRecording}
          rerecordable={false}
          recordingwidth={1280}
          recordingheight={720}
          picksnapshots={false}
          preventReRenderOnUpdate={false}
          onRecording={data => {
            recordEvent(data);
            onRecording();
          }}
          onUploading={data => {
            if (setUploadStarted) setUploadStarted();
            uploadEvent(data);
            onUploading();
          }}
          mediaLocales={mediaLocales}
          onVerified={data => {
            if (setUploadingComplete) setUploadingComplete();
            verifiedEvent(data);
            const dataToReturn = {
              token: data.video,
              url: ziggeoPlayerUrl + data.video,
              provider: 'ziggeo',
            };
            saveMedia(dataToReturn);
          }}
          onUploadSelected={file => {
            if (typeof setShowRecorder === 'function') setShowRecorder(false);
            if (file && handleBadUploadType) {
              //eslint-disable-next-line
              const type = file?.video_data?.video_file_name?.split('.').pop();
              if (!fileTypes.includes(type?.toLowerCase()))
                handleBadUploadType();
            }
          }}
          video-profile={VIDEO_PROFILE}
          theme="modern"
          themecolor="black"
        />
      </Suspense>
    </div>
  );
};

ZiggeoMediaRecorder.propTypes = {
  apiKey: PropTypes.string.isRequired,
  recordWebcamEvent: PropTypes.func,
  handleBadUploadType: PropTypes.func,
  saveMedia: PropTypes.func,
  setUploadStarted: PropTypes.func,
  setUploadingComplete: PropTypes.func,
  isScreenRecording: PropTypes.bool,
  isAudioRecording: PropTypes.bool,
  webcamPlaybackEnabled: PropTypes.bool,
};

export default ZiggeoMediaRecorder;
